/**
 * Single source of truth for the audit log's curated subject/action values. The
 * same lists drive both the i18n labels (`audit_log.subject.*` / `audit_log.action.*`)
 * and the filter dropdowns. `subject`/`action` are free strings on the wire, so any
 * value outside these lists still renders (humanized raw fallback) but cannot be filtered.
 */

import { EMPTY_VALUE } from '@/utils/formatters'

export const auditLogSubjects = [
	'person',
	'project',
	'project_document',
	'project_person',
	'project_product',
	'project_benchmark',
	'supplier',
	'product',
	'certificate',
	'instrument',
	'material',
	'node_component_type',
	'panel_account',
	'network',
	'network_node',
	'network_connection',
	'network_element_person',
	'app_account',
	'work_status_update',
	'element_document',
	'measurement',
] as const

/**
 * Audited attribute names, straight off the wire (model column names plus the attachment
 * fields recorded by `record_attachment_replacement!`). Anything outside this list still
 * renders through `humanizeToken`, which reads as English.
 */
export const auditLogAttributes = [
	'additional_info',
	'address',
	'assigned_by_id',
	'cascade',
	'category',
	'comment',
	'company_name',
	'composition_material',
	'created_by_id',
	'delivered_quantity',
	'description',
	'diameter',
	'diameter_kind',
	'discarded_at',
	'elevation',
	'email',
	'ends_at',
	'expires_at',
	'file',
	'height',
	'input_node_id',
	'input_ordinate',
	'label',
	'length',
	'material_card',
	'name',
	'network_element_id',
	'network_element_type',
	'network_id',
	'network_node_id',
	'node_component_type_id',
	'node_type',
	'ordinate_bottom',
	'ordinate_top',
	'output_node_angle',
	'output_node_id',
	'output_ordinate',
	'person_id',
	'phone',
	'photos',
	'predefined',
	'public',
	'product_category',
	'product_id',
	'product_specification_id',
	'project_id',
	'quantity',
	'required_quantity',
	'requires_certificate',
	'resolved',
	'resolved_at',
	'resolved_by_id',
	'role',
	'rotation',
	'serial_number',
	'size',
	'size_description',
	'starts_at',
	'status',
	'statusable_id',
	'statusable_type',
	'supplier_id',
	'terrain_type',
	'tilt',
	'type',
	'unit',
	'vat_id',
	'wall_thickness',
	'warning',
] as const

/** Data operations only — session events (login/logout/refresh) are deliberately not audited. */
export const auditLogActions = ['create', 'update', 'destroy'] as const

export type AuditLogSubject = (typeof auditLogSubjects)[number]
export type AuditLogAttribute = (typeof auditLogAttributes)[number]
export type AuditLogAction = (typeof auditLogActions)[number]

/** `'all'` is the filter sentinel meaning "no filter"; mirrors the users `role` filter. */
export const subjectFilterValues = ['all', ...auditLogSubjects] as const
export const actionFilterValues = ['all', ...auditLogActions] as const

export type SubjectFilterValue = (typeof subjectFilterValues)[number]
export type ActionFilterValue = (typeof actionFilterValues)[number]

export function isKnownSubject(value: string): value is AuditLogSubject {
	return (auditLogSubjects as readonly string[]).includes(value)
}

export function isKnownAction(value: string): value is AuditLogAction {
	return (auditLogActions as readonly string[]).includes(value)
}

export function isKnownAttribute(value: string): value is AuditLogAttribute {
	return (auditLogAttributes as readonly string[]).includes(value)
}

/** Humanizes a raw token (snake_case attribute key, or an unknown subject/action) into a readable label. */
export function humanizeToken(value: string): string {
	const words = value.replace(/[_\s]+/g, ' ').trim()
	if (words.length === 0) return EMPTY_VALUE
	return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Renders an untyped audit `[before, after]` change value. Deliberately dumb in v1. */
export function formatChangeValue(value: unknown): string {
	if (value === null || value === undefined) return EMPTY_VALUE
	if (typeof value === 'string') return value.trim().length === 0 ? EMPTY_VALUE : value
	if (typeof value === 'number' || typeof value === 'boolean') return String(value)
	return JSON.stringify(value)
}
